import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Editor for how this workspace handles bugs.
 *
 * The settings that matter here are the two that shape behaviour rather than
 * wiring: how long a bug lasts untouched, and how many interruptions a cycle is
 * expected to absorb. Both are deliberately visible rather than buried, because
 * they are the numbers that decide whether the pool stays a pool or turns back
 * into a backlog.
 */
const BugSettings = ({
  value,
  defaults = {},
  jiraEnabled = false,
  canManage = false,
  saving = false,
  onSave,
}) => {
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  const set = (key) => (event) => {
    const next = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setDraft(prev => ({ ...prev, [key]: next }));
  };

  const dirty = JSON.stringify(draft) !== JSON.stringify(value);
  const fadeDays = Number(draft.bugFadeAfterDays);
  const budget = Number(draft.emergencyBudgetPerCycle);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Bugs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        A pool rather than a backlog. Bugs are triaged into a decision — fix it in
        cool-down, shape it into a pitch, treat it as an emergency, or let it go —
        and there is deliberately no priority field, because a priority is a
        decision deferred rather than made.
      </Typography>

      <FormControlLabel
        control={(
          <Switch
            checked={draft.bugTrackingEnabled !== false}
            onChange={set('bugTrackingEnabled')}
            disabled={!canManage || saving}
          />
        )}
        label={draft.bugTrackingEnabled !== false ? 'Keep a bug pool' : 'Bug pool off'}
      />

      {draft.bugTrackingEnabled !== false && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Bugs fade after"
              value={draft.bugFadeAfterDays ?? ''}
              onChange={set('bugFadeAfterDays')}
              disabled={!canManage || saving}
              inputProps={{ min: 0, max: 365 }}
              helperText={
                fadeDays === 0
                  ? 'Never — nothing fades, sweep by hand'
                  : `${fadeDays} days without anyone touching it (default ${defaults.bugFadeAfterDays ?? 56}, a cycle plus cool-down)`
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Emergencies a cycle can absorb"
              value={draft.emergencyBudgetPerCycle ?? ''}
              onChange={set('emergencyBudgetPerCycle')}
              disabled={!canManage || saving}
              inputProps={{ min: 0, max: 20 }}
              helperText={
                budget === 0
                  ? 'Every interruption gets flagged'
                  : `Past ${budget} in one cycle, the count is flagged (default ${defaults.emergencyBudgetPerCycle ?? 2})`
              }
            />
          </Grid>

          {jiraEnabled && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>From Jira</Typography>
                <Typography variant="caption" color="text.secondary">
                  Jira is the inbox: bugs reported there appear in the pool, and
                  fixing one here moves the Jira issue. Triage decisions are made
                  here and are never overwritten by a sync.
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Issue type"
                  value={draft.bugIssueType || ''}
                  onChange={set('bugIssueType')}
                  disabled={!canManage || saving}
                  placeholder={defaults.bugIssueType || 'Bug'}
                  helperText="What this project calls a bug"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Status when fixed"
                  value={draft.bugDoneStatus || ''}
                  onChange={set('bugDoneStatus')}
                  disabled={!canManage || saving}
                  placeholder="Done"
                  helperText="Blank resolves by transition category, which works on most workflows"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Inbox query (JQL)"
                  value={draft.bugJql || ''}
                  onChange={set('bugJql')}
                  disabled={!canManage || saving}
                  placeholder={`project = "ABC" AND issuetype = "${draft.bugIssueType || 'Bug'}" AND statusCategory != Done`}
                  helperText="Only needed when issue type alone cannot identify your bugs — a label, a component, one board. Blank builds the default query."
                />
              </Grid>
            </>
          )}

          {!jiraEnabled && (
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                Jira sync is off, so bugs are reported and fixed here. Turn Jira on
                to have bugs filed there show up in the pool as well.
              </Alert>
            </Grid>
          )}
        </Grid>
      )}

      {canManage && (
        <Button
          variant="contained"
          sx={{ mt: 3 }}
          onClick={() => onSave(draft)}
          disabled={saving || !dirty}
        >
          {saving ? <CircularProgress size={24} /> : 'Save bug settings'}
        </Button>
      )}
    </Box>
  );
};

export default BugSettings;
